import { FormInput } from "@general-backoffice/core";
import type { ExchangeAccount as ApiExchangeAccount, ValidateServerResult } from "@investhinks/api-client";
import { exchangeAccountsEndpoints } from "@investhinks/api-client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useFormHandlers } from "../../hooks/strapi";
import xm_account_img from "../../media/misc/xm_account.png";
import xm_account_details_img from "../../media/misc/xm_account_details.png";
import xm_account_password_reset_img from "../../media/misc/xm_account_password_reset.png";
import xm_account_password_step_img from "../../media/misc/xm_account_password_step.png";
import ExchangeSelect from "../exchanges/ExchangeSelect";
import ModalInfoFormContainer from "../shared/ModalInfoFormContainer";
import LeverageSelectionInput, { LeverageInfoModal } from "./LeverageSelectionInput";

const CenteredImg = ({ v, k }: { v: string; k: string }) => (
  <div className="w-100 text-center flex-center">
    <img src={v} alt={k} className="mw-100 mt-2 mb-3" style={{ maxHeight: 350 }} />
  </div>
);

const transComps = Object.entries({
  xm_account_img,
  xm_account_details_img,
  xm_account_password_reset_img,
  xm_account_password_step_img,
}).reduce(
  (r, [k, v]) => ({
    ...r,
    [k.toUpperCase()]: <CenteredImg v={v} k={k} />,
  }),
  {}
);
Object.assign(transComps, { ul: <ul />, li: <li /> });

// ExchangeAccountData extends api-client ExchangeAccount with form-only fields.
// The credentials field is not returned by the API (for security) but is needed for forms.
type ExchangeAccountData = ApiExchangeAccount & {
  credentials?: {
    username?: string;
    password?: string;
    server?: string;
    serverConfirmed?: boolean;
  } | null;
};

interface UpdateExchangeAccountModalProps {
  exchangeAccount: ExchangeAccountData | null;
  setExchangeAccount: React.Dispatch<React.SetStateAction<ExchangeAccountData | null>>;
  close: () => void;
  update: () => Promise<void>;
  isLoading: boolean;
}

const UpdateExchangeAccountModal = ({
  exchangeAccount,
  setExchangeAccount,
  close,
  update,
  isLoading,
}: UpdateExchangeAccountModalProps) => {
  const { t } = useTranslation("exchangeAccounts", { keyPrefix: "updateExchangeAccountModal" });
  const { id, name, exchange, credentials, status, defaultLeverage, error, accountId } = exchangeAccount || {};
  const {
    // Tradfi
    username,
    password,
    server,
    serverConfirmed,
    // Crypto
  } = credentials || {};

  // Use accountId as fallback for username (accountId is stored at root level after account creation)
  const tradingAccountId = username || accountId || "";

  // Modals
  const [showInfoModal, setInfoModal] = useState<string | null>(null);

  const isRepairMode = !!id && status === "INVALID";
  const { handleInputChange, handleInputEvent } = useFormHandlers(setExchangeAccount);

  // MT5 server name pre-check (free-text server field only): catches an account number or a typo
  // before the account is created, instead of an "invalid credentials" state after validation.
  const [serverCheck, setServerCheck] = useState<ValidateServerResult | null>(null);
  const [isCheckingServer, setCheckingServer] = useState(false);
  const checkServer = async (): Promise<boolean> => {
    if (!isTradfi || serverOptions || !server?.trim()) return true;
    setCheckingServer(true);
    try {
      const result = await exchangeAccountsEndpoints.validateServer({
        server,
        login: tradingAccountId || undefined,
      });
      setServerCheck(result);
      return result.ok || (result.reason === "NOT_FOUND" && !!serverConfirmed);
    } catch {
      // The pre-check is advisory; the API repeats it on submit.
      setServerCheck(null);
      return true;
    } finally {
      setCheckingServer(false);
    }
  };
  const serverError = serverCheck && !serverCheck.ok && !(serverCheck.reason === "NOT_FOUND" && serverConfirmed)
    ? serverCheck
    : null;
  const setServer = (value: string) => {
    setServerCheck(null);
    handleInputChange("credentials.server", value);
    handleInputChange("credentials.serverConfirmed", undefined);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!(await checkServer())) return;
    await update();
  };

  // Store handleInputChange in ref to use in effects without causing loops
  const handleInputChangeRef = useRef(handleInputChange);
  useEffect(() => {
    handleInputChangeRef.current = handleInputChange;
  }, [handleInputChange]);

  // Initialization flags - reset when exchange account changes
  const initRefs = useRef({
    leverage: false,
    username: false,
  });
  useEffect(() => {
    initRefs.current = {
      leverage: false,
      username: false,
    };
  }, [exchangeAccount?.id]);

  const exchangeName = exchange?.name;
  const exchangeError = exchangeAccount?.error;
  const { title, info } = useMemo(() => {
    if (exchangeName === "XM" && status === "INVALID") {
      switch (error) {
        case "INVALID_CREDENTIALS":
          return {
            title: <Trans ns="dashboard" i18nKey={`exchangeAccount.validationErrorsText.${exchangeError || "NONE"}`} />,
            info: <Trans t={t} i18nKey="info.xm.INVALID_CREDENTIALS" components={transComps} />,
          };
        default:
          return {};
      }
    }
    return {};
  }, [exchangeName, status, error, exchangeError, t]);

  const exchangeServers = exchange?.servers;
  const serverOptions = useMemo(() => {
    if (!exchangeServers?.length) return null;
    else return exchangeServers.map((s) => s?.url);
  }, [exchangeServers]);

  // Always react to exchange changes — auto-fill when exactly 1 server, clear otherwise.
  // No initRefs guard: server must update when user picks a different exchange.
  useEffect(() => {
    handleInputChangeRef.current("credentials.server", serverOptions?.length === 1 ? serverOptions[0] : undefined);
  }, [serverOptions]);

  const isTradfi = exchange?.type === "tradfi";

  // Initialize credentials.username from accountId for existing tradfi accounts
  useEffect(() => {
    if (!initRefs.current.username && id && isTradfi && accountId && !username) {
      initRefs.current.username = true;
      handleInputChangeRef.current("credentials.username", accountId);
    }
  }, [id, isTradfi, accountId, username]);

  // Ensure defaultLeverage is set when editing an existing account
  const exchangeAccountDefaultLeverage = exchangeAccount?.defaultLeverage;
  useEffect(() => {
    if (!initRefs.current.leverage && id && exchangeAccount && !defaultLeverage && exchangeAccountDefaultLeverage) {
      initRefs.current.leverage = true;
      handleInputChangeRef.current("defaultLeverage", exchangeAccountDefaultLeverage);
    }
  }, [id, exchangeAccount, defaultLeverage, exchangeAccountDefaultLeverage]);

  // Server is required unless exactly 1 server is pre-defined (auto-filled silently)
  const isServerRequired = serverOptions?.length !== 1;

  // Form validation
  const isFormValid = useMemo(() => {
    // For new accounts, leverage is required
    if (!id && !isRepairMode && !defaultLeverage) return false;

    // Exchange/broker is always required
    if (!exchange) return false;

    // For tradfi accounts, check credentials
    if (isTradfi) {
      if (!tradingAccountId) return false;
      if (!password) return false;
      if (isServerRequired && !server) return false;
    }

    return true;
  }, [id, isRepairMode, defaultLeverage, exchange, isTradfi, tradingAccountId, password, server, isServerRequired]);

  const areLoading = isLoading;
  return (
    <>
      <LeverageInfoModal isOpen={showInfoModal === "leverage"} close={() => setInfoModal(null)} />

      <ModalInfoFormContainer
        size="md"
        isOpen={!!exchangeAccount}
        toggleOpen={close}
        closeText={t("closeBtn")}
        buttonSave={{
          children: id ? t("submitBtn.update") : t("submitBtn.create"),
          disabled: areLoading || isCheckingServer || !isFormValid,
        }}
        isLoading={areLoading}
        onSubmit={handleSubmit}
        info={info}
        infoTitle={title}
        infoColor="danger"
        hideHeader
        fullscreenMobile
      >
        <h5 className="mb-3 px-1">{id ? t("title.update", [name]) : t("title.create")}</h5>
        {!isRepairMode && !id && (
          <LeverageSelectionInput
            label={<>{t("form.leverage")}</>}
            onChange={(maxLeverage: number, _minLeverage: number) =>
              handleInputChange("defaultLeverage", maxLeverage || null)
            }
            leverage={defaultLeverage}
            isLoading={isLoading}
            required
          />
        )}

        {/* Broker */}
        <ExchangeSelect
          label={t("form.broker")}
          exchange={exchange ?? null}
          leverage={defaultLeverage ?? undefined}
          setExchange={(newExchange) => {
            if (!!id && !newExchange) {
              return;
            }
            handleInputChange("exchange", newExchange);
          }}
          isDisabled={!defaultLeverage || areLoading || !!id}
          required
          description={
            !id &&
            exchange?.link && (
              <Trans
                t={t}
                i18nKey="form.descriptions.broker"
                components={[
                  <span
                    key="0"
                    onClick={() => window.open(exchange.link ?? undefined, "_blank")}
                    className="cursor-pointer text-primary"
                  />,
                ]}
              />
            )
          }
        />

        {/* Name */}
        <FormInput.Input
          name="name"
          label={t("form.name")}
          autoComplete="off"
          value={name ?? ""}
          placeholder={t("form.placeholderName")}
          onChange={handleInputEvent}
          disabled={areLoading}
        />

        {isTradfi && (
          <>
            {/* Trading credentials */}
            <FormInput.Input
              name="credentials.username"
              label={t("form.tradingAccountId")}
              autoComplete="off"
              value={tradingAccountId}
              onChange={handleInputEvent}
              disabled={areLoading || (!!id && !isRepairMode)}
              pattern="[0-9]+"
              required
              description={t("form.descriptions.tradingAccountId")}
            />

            <FormInput.Password
              name="credentials.password"
              label={t("form.tradingAccountPassword")}
              autoComplete="new-password"
              value={password}
              onChange={handleInputEvent}
              disabled={areLoading}
              required
            />

            {/* Server field: free-text when no pre-defined servers, dropdown when 2+ servers, hidden when exactly 1 (auto-filled) */}
            {!serverOptions && (
              <FormInput.Input
                name="credentials.server"
                label={t("form.tradingAccountServer")}
                value={server}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => setServer(event.target.value)}
                onBlur={() => void checkServer()}
                disabled={areLoading}
                invalid={!!serverError}
                error={
                  serverError
                    ? serverError.reason === "SYNTAX"
                      ? t(`form.serverErrors.${serverError.issue === "LOOKS_LIKE_LOGIN" ? "looksLikeLogin" : "syntax"}`)
                      : t("form.serverErrors.notFound")
                    : undefined
                }
                description={
                  serverError?.reason === "NOT_FOUND" ? (
                    <span>
                      {serverError.suggestions.length > 0 && (
                        <>
                          {t("form.serverErrors.didYouMean")}{" "}
                          {serverError.suggestions.map((suggestion, index) => (
                            <React.Fragment key={suggestion}>
                              {index > 0 && ", "}
                              <span className="cursor-pointer text-primary" onClick={() => setServer(suggestion)}>
                                {suggestion}
                              </span>
                            </React.Fragment>
                          ))}
                          {". "}
                        </>
                      )}
                      <span
                        className="cursor-pointer text-muted"
                        style={{ textDecoration: "underline" }}
                        onClick={() => handleInputChange("credentials.serverConfirmed", true)}
                      >
                        {t("form.serverErrors.useAnyway")}
                      </span>
                    </span>
                  ) : (
                    t("form.descriptions.tradingAccountServer")
                  )
                }
                required
              />
            )}
            {serverOptions && serverOptions.length > 1 && (
              <FormInput.Select
                label={t("form.tradingAccountServer")}
                options={serverOptions.map((s) => ({ value: s, label: s }))}
                value={server ? { value: server, label: server } : null}
                onChange={(option: { value: string; label: string } | null) =>
                  handleInputChange("credentials.server", option?.value ?? null)
                }
                isDisabled={areLoading}
                required
              />
            )}
          </>
        )}
      </ModalInfoFormContainer>
      {/* <Modal.FormContainer
      title={!!id ? t("title.update", [name]) : t("title.create")}
      isOpen={!!exchangeAccount}
      toggleOpen={close}
      closeText={t("closeBtn")}
      buttonSave={{ children: id ? t("submitBtn.update") : t("submitBtn.create"), disabled: areLoading }}
      isLoading={areLoading}
      onSubmit={handleSubmit}
    >
    
      <ExchangeSelect
        label={t("form.broker")}
        exchange={exchange}
        setExchange={exchange => handleInputChange('exchange', exchange)}
        isDisabled={areLoading || !!id}
        required
        description={exchange?.link && <>
          <Trans
            t={t} i18nKey="form.descriptions.broker"
            components={[
              <span
                onClick={() => window.open(exchange.link ?? undefined, "_blank")}
                className="cursor-pointer text-primary ml-1"
              />
            ]}
          />
        </>}
      />
    
      <FormInput.Input
        name="name"
        label={t("form.name")}
        autoComplete="off"
        value={name}
        placeholder={t("form.placeholderName")}
        onChange={handleInputEvent}
        disabled={areLoading}
        description={t("form.descriptions.name")}
      // required
      />
    
      {!!exchange && <FormDecoration.Line />}
    
      {isTradfi ? <>
        <FormInput.Input
          name="credentials.username"
          label={t("form.tradingAccountId")}
          autoComplete="off"
          value={username}
          onChange={handleInputEvent}
          disabled={areLoading || !!id}
          pattern="[0-9]+"
          required
          description={t("form.descriptions.tradingAccountId")}
        />
    
        <FormInput.Password
          name="credentials.password"
          label={t("form.tradingAccountPassword")}
          autoComplete="new-password"
          value={password}
          onChange={handleInputEvent}
          disabled={areLoading}
          required
        />
    
        <FormInput.Input
          name="credentials.server"
          label={t("form.tradingAccountServer")}
          value={server}
          onChange={handleInputEvent}
          disabled={areLoading}
          required
        />
      </> : <>
    
      </>}
    
    </Modal.FormContainer> */}
    </>
  );
};

export default UpdateExchangeAccountModal;
